import React from 'react';
import {
    buyerActiveItemsColumns,
    buyerItemsLostColumns,
    buyerPurchasesColumns,
    sellerActiveColumns,
    sellerSoldColumns,
    sellerUnsoldColumns,
    payments2Columns
} from './../constants/column-definitions';
import { PAGE_LIST_TYPES } from '../constants/list-types';
import { PAGE_NAMES } from '../constants/page';
import { getMyTraderaUrls } from 'tradera-apps/my-tradera/app/pages/routes';
import HeartIcon from '@tradera/blueprint/theme/icons2/heart.svg?jsx';
import BookmarkIcon from '@tradera/blueprint/theme/icons2/bookmark.svg?jsx';
import StarIcon from '@tradera/blueprint/theme/icons2/star.svg?jsx';
import MessageIcon from '@tradera/blueprint/theme/icons2/message.svg?jsx';
import BidLostIcon from '@tradera/blueprint/theme/icons2/bid-lost.svg?jsx';
import TagIcon from '@tradera/blueprint/theme/icons2/sale.svg?jsx';
import ListIcon from '@tradera/blueprint/theme/icons2/list.svg?jsx';
import WriteIcon from '@tradera/blueprint/theme/icons2/write.svg?jsx';
import SettingsIcon from '@tradera/blueprint/theme/icons2/settings.svg?jsx';
import BiddingIcon from '@tradera/blueprint/theme/icons2/auction.svg?jsx';
import WonIcon from '@tradera/blueprint/theme/icons2/winning.svg?jsx';
import DocumentIcon from '@tradera/blueprint/theme/icons2/document.svg?jsx';
import PenIcon from '@tradera/blueprint/theme/icons2/pen.svg?jsx';
import RatingPositiveIcon from '@tradera/blueprint/theme/icons2/rating-positive.svg?jsx';
import SoldIcon from '@tradera/blueprint/theme/icons2/sold.svg?jsx';
import LockIcon from '@tradera/blueprint/theme/icons2/lock.svg?jsx';
import UnsoldIcon from '@tradera/blueprint/theme/icons2/unsold.svg?jsx';
import UserIcon from '@tradera/blueprint/theme/icons2/user.svg?jsx';
import WalletIcon from '@tradera/blueprint/theme/icons2/wallet.svg?jsx';
import PackageIcon from '@tradera/blueprint/theme/icons2/package.svg?jsx';

/**
 * This is default information used to differentiate UI elements based on different pages.
 * @returns {object}
 */
export const routeInformationFactory = (t, toLocalizedUrl) => {
    const urls = getMyTraderaUrls(toLocalizedUrl);
    return {
        [urls.overview]: {
            url: toLocalizedUrl('/my/overview'),
            class: 'myt-menu-list-item-subtitle myt-menu-list-item-overview',
            label: t('myt_overview_label'),
            isExternal: true
        },
        [urls.wishlist]: {
            url: toLocalizedUrl('/my/wish-list'),
            title: t('myt_wishlist_title'),
            isExternal: true,
            icon: <HeartIcon className="myt-menu-list-item-icon" />
        },
        [urls.savedSearches]: {
            url: toLocalizedUrl('/my/saved-searches'),
            title: t('myt_savedSearches_title'),
            isExternal: true,
            icon: <BookmarkIcon className="myt-menu-list-item-icon" />
        },
        [urls.favouriteSellers]: {
            url: toLocalizedUrl('/my/favourite-sellers'),
            title: t('myt_favouriteSellers_title'),
            isExternal: true,
            icon: <StarIcon className="myt-menu-list-item-icon" />,
            showBasedOnInitData: initData =>
                !initData?.featureSwitches['my-tradera-seller-list']
        },
        [urls.sellerList]: {
            url: toLocalizedUrl('/my/seller-list'),
            title: t('myt_sellerList_title'),
            isExternal: false,
            icon: <StarIcon className="myt-menu-list-item-icon" />,
            overview: urls.overview,
            showBasedOnInitData: initData =>
                initData?.featureSwitches['my-tradera-seller-list']
        },
        [urls.activeItems]: {
            isBuyerPage: true,
            searchUrl: toLocalizedUrl('/my/api/buyer/active'),
            pageName: PAGE_NAMES.BuyerActiveItems,
            listType: PAGE_LIST_TYPES.BUYER_ACTIVE_ITEMS,
            url: toLocalizedUrl('/my/active-items'),
            title: t('myt_activeItems_title'),
            columns: buyerActiveItemsColumns,
            overview: urls.buyerOverview,
            hasLabels: false,
            shouldShowCompact: false,
            shouldShowListToggle: false,
            gaLabel: 'Pågående budgivningar',
            bulkEditEnabled: true,
            icon: <BiddingIcon className="myt-menu-list-item-icon" />,
            noResultsText: t('myt_activeItems_noResultsText')
        },
        [urls.itemsLost]: {
            isBuyerPage: true,
            searchUrl: toLocalizedUrl('/my/api/buyer/items-lost'),
            pageName: PAGE_NAMES.BuyerItemsLost,
            listType: PAGE_LIST_TYPES.BUYER_ITEMS_LOST,
            overview: urls.buyerOverview,
            url: toLocalizedUrl('/my/items-lost'),
            title: t('myt_lostItems_title'),
            columns: buyerItemsLostColumns,
            hasLabels: false,
            shouldShowCompact: false,
            shouldShowListToggle: false,
            bulkEditEnabled: true,
            icon: <BidLostIcon className="myt-menu-list-item-icon" />,
            noResultsText: t('myt_lostItems_noResultsText')
        },
        [urls.selling]: {
            url: toLocalizedUrl('/selling'),
            title: t('siteHeader_createItem'),
            isExternal: true,
            icon: <PenIcon className="myt-menu-list-item-icon" />
        },
        [urls.active]: {
            searchUrl: toLocalizedUrl('/my/api/seller/active'),
            pageName: PAGE_NAMES.SellerActive,
            listType: PAGE_LIST_TYPES.SELLER_ACTIVE,
            url: toLocalizedUrl('/my/active'),
            overview: urls.sellerOverview,
            title: t('myt_activeListings_title'),
            columns: sellerActiveColumns,
            hasLabels: false,
            shouldShowCompact: true,
            shouldShowListToggle: true,
            stats: {
                FastPris: {
                    label: 'Fast pris',
                    value: '0'
                },
                Auktioner: {
                    label: 'Auktioner',
                    value: '0'
                },
                Bud: {
                    label: 'Bud',
                    value: '0'
                },
                SummaUtropspris: {
                    label: 'Summa utropspris',
                    value: '0'
                },
                SummaBud: {
                    label: 'Summa bud',
                    value: '0'
                }
            },
            bulkEditEnabled: true,
            icon: <TagIcon className="myt-menu-list-item-icon" />,
            noResultsText: t('myt_activeListings_noResultsText')
        },
        [urls.sold]: {
            searchUrl: toLocalizedUrl('/my/api/seller/sold'),
            searchUrlNormalView: toLocalizedUrl(
                '/api/webapi/mytradera/seller/soldpurchaseorders'
            ),
            pageName: PAGE_NAMES.SellerSold,
            listType: PAGE_LIST_TYPES.SELLER_SOLD,
            url: toLocalizedUrl('/my/sold'),
            title: t('myt_soldItems_title'),
            columns: sellerSoldColumns,
            overview: urls.sellerOverview,
            hasLabels: true,
            shouldShowCompact: true,
            shouldShowListToggle: true,
            stats: {
                Butiksobjekt: {
                    label: 'Butiksobjekt',
                    value: '0'
                },
                FastPris: {
                    label: 'Fast pris',
                    value: '0'
                },
                Auktioner: {
                    label: 'Auktioner',
                    value: '0'
                },
                Bud: {
                    label: 'Bud',
                    value: '0t'
                },
                SummaUtropspris: {
                    label: 'Summa utropspris',
                    value: '0'
                },
                SummaBud: {
                    label: 'Summa bud',
                    value: '0'
                }
            },
            bulkEditEnabled: false,
            icon: <SoldIcon className="myt-menu-list-item-icon" />,
            noResultsText: t('myt_soldItems_noResultsText')
        },
        [urls.unsold]: {
            searchUrl: toLocalizedUrl('/my/api/seller/unsold'),
            pageName: PAGE_NAMES.SellerUnsold,
            listType: PAGE_LIST_TYPES.SELLER_NOT_SOLD,
            overview: urls.sellerOverview,
            url: toLocalizedUrl('/my/unsold'),
            title: t('myt_unsoldItems_title'),
            columns: sellerUnsoldColumns,
            shouldShowCompact: true,
            shouldShowListToggle: true,
            stats: {},
            bulkEditEnabled: true,
            icon: <UnsoldIcon className="myt-menu-list-item-icon" />,
            noResultsText: t('myt_unsoldItems_noResultsText')
        },
        [urls.purchases]: {
            isBuyerPage: true,
            searchUrl: toLocalizedUrl('/my/api/buyer/purchases'),
            searchUrlNormalView: toLocalizedUrl(
                '/api/webapi/mytradera/buyer/boughtpurchaseorders'
            ),
            pageName: PAGE_NAMES.BuyerPurchases,
            listType: PAGE_LIST_TYPES.BUYER_PURCHASES,
            url: toLocalizedUrl('/my/purchases'),
            overview: urls.buyerOverview,
            title: t('myt_boughtItems_title'),
            columns: buyerPurchasesColumns,
            shouldShowCompact: false,
            shouldShowListToggle: true,
            stats: {},
            bulkEditEnabled: true,
            icon: <WonIcon className="myt-menu-list-item-icon" />,
            noResultsText: t('myt_boughtItems_noResultsText')
        },
        [urls.pickList]: {
            url: toLocalizedUrl('/my/picklist'),
            title: t('myt_picklist_title'),
            isExternal: true,
            icon: <ListIcon className="myt-menu-list-item-icon" />
        },
        [urls.buyerFeedback]: {
            pageName: PAGE_NAMES.MyTraderaLeaveFeedback,
            url: toLocalizedUrl('/my/leave-feedback/buyer?transactionId=0'),
            title: t('myt_buyerFeedback_title'),
            isExternal: true,
            icon: <WriteIcon className="myt-menu-list-item-icon" />,
            hasCustomHeader: true,
            overview: urls.buyerOverview
        },
        [urls.sellerFeedback]: {
            pageName: PAGE_NAMES.MyTraderaLeaveFeedback,
            url: toLocalizedUrl('/my/leave-feedback/seller?transactionId=0'),
            title: t('myt_sellerFeedback_title'),
            isExternal: true,
            icon: <WriteIcon className="myt-menu-list-item-icon" />,
            hasCustomHeader: true,
            overview: urls.sellerOverview
        },
        [urls.myFeedback]: {
            url: toLocalizedUrl('/getUrlMyFeedback'),
            title: t('myt_myFeedback_title'),
            isExternal: true,
            icon: <RatingPositiveIcon className="myt-menu-list-item-icon" />
        },
        [urls.settings]: {
            url: urls.settings,
            title: t('myt_settings_title'),
            pageName: PAGE_NAMES.Settings,
            overview: urls.accountOverview,
            isExternal: false,
            icon: <SettingsIcon className="myt-menu-list-item-icon" />
        },
        [urls.settingsAccount]: {
            url: urls.settingsAccount,
            title: t('myt_settings_account_title'),
            subTitle: t('myt_settings_account_subTitle'),
            pageName: PAGE_NAMES.SettingsAccount,
            overview: urls.settings,
            isInternalOverview: true,
            isExternal: false,
            icon: <UserIcon className="icon icon-md ml-auto" />
        },
        [urls.settingsEmail]: {
            url: urls.settingsEmail,
            title: t('myt_settings_email_title'),
            subTitle: t('myt_settings_email_subTitle'),
            pageName: PAGE_NAMES.SettingsEmail,
            overview: urls.settings,
            isInternalOverview: true,
            isExternal: false,
            icon: <MessageIcon className="icon icon-md ml-auto" />
        },
        [urls.settingsSelling]: {
            url: urls.settingsSelling,
            title: t('myt_settings_selling_title'),
            subTitle: t('myt_settings_selling_subTitle'),
            pageName: PAGE_NAMES.SettingsSeller,
            overview: urls.settings,
            isInternalOverview: true,
            isExternal: false,
            icon: <TagIcon className="icon icon-md ml-auto" />
        },
        [urls.settingsNotifications]: {
            url: urls.settingsNotifications,
            title: t('myt_settings_notifications_title'),
            subTitle: t('myt_settings_notifications_subTitle'),
            pageName: PAGE_NAMES.SettingsNotifications,
            overview: urls.settings,
            isInternalOverview: true,
            isExternal: false,
            icon: <ListIcon className="icon icon-md ml-auto" />
        },
        [urls.settingsSecurity]: {
            url: urls.settingsSecurity,
            title: t('myt_settings_security_title'),
            subTitle: t('myt_settings_security_subTitle'),
            pageName: PAGE_NAMES.SettingsSecurity,
            overview: urls.settings,
            isInternalOverview: true,
            isExternal: false,
            icon: <LockIcon className="icon icon-md ml-auto" />
        },
        [urls.settingsPayouts]: {
            url: urls.settingsPayouts,
            title: t('myt_settings_payouts_title'),
            subTitle: t('myt_settings_payouts_subTitle'),
            pageName: PAGE_NAMES.Settings,
            overview: urls.settings,
            isInternalOverview: true,
            isExternal: false,
            icon: <WalletIcon className="icon icon-md ml-auto" />
        },
        [urls.invoices]: {
            url: toLocalizedUrl('/my/Invoices?page=1'),
            title: t('myt_invoices_title'),
            isExternal: true,
            icon: <DocumentIcon className="myt-menu-list-item-icon" />
        },
        [urls.payments]: {
            url: toLocalizedUrl('/my/Payments'),
            title: t('myt_payments_title'),
            isExternal: true,
            showBasedOnInitData: initData => {
                return (
                    initData &&
                    initData.paymentSettings &&
                    initData.paymentSettings.showPayments1Menu === true &&
                    initData.paymentSettings.showPayments2Menu === false
                );
            },
            icon: <ListIcon className="myt-menu-list-item-icon" />
        },
        [urls.payments2]: {
            url: toLocalizedUrl('/my/Payments2'),
            title: t('myt_payments_title'),
            isExternal: true,
            overview: urls.accountOverview,
            showBasedOnInitData: initData => {
                return (
                    initData?.paymentSettings?.showPayments2Menu &&
                    !initData?.hasWallet
                );
            },
            icon: <ListIcon className="myt-menu-list-item-icon" />,
            columns: payments2Columns
        },
        [urls.payouts]: {
            url: urls.payouts,
            title: t('myt_payouts_title'),
            pageName: PAGE_NAMES.Payouts,
            overview: urls.accountOverview,
            showBasedOnInitData: initData =>
                initData?.paymentSettings?.showPayments2Menu &&
                initData?.hasWallet,
            isExternal: false,
            icon: <WalletIcon className="myt-menu-list-item-icon" />
        },
        [urls.messaging]: {
            url: toLocalizedUrl('/messaging'),
            label: t('myt_messaging_label'),
            class: 'myt-menu-list-item-subtitle',
            hideMainNav: true,
            isExternal: true,
            icon: <MessageIcon className="myt-menu-list-item-icon" />
        },
        [urls.buyShippingBags]: {
            url: toLocalizedUrl('/tradera-shop/shipping-bags'),
            title: t('myt_buyShippingBags_title'),
            isExternal: true,
            icon: <PackageIcon className="myt-menu-list-item-icon" />,
            showBasedOnInitData: initData =>
                initData?.featureSwitches['tradera-shop']
        }
    };
};
